import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { usePreference, setPreference } from '@fs/zion-preferences'
import { useFeatureFlag } from '@fs/zion-flags'
import { useOverlayContext } from '../overlayContextProvider/OverlayContext'

const SHOW_HIGHLIGHTS_PREFERENCE_KEY = 'shared-image-viewer.showHighlights'

const DEFAULT_HIGHLIGHTS_VISIBILITY_CONTEXT = {
  toggleChecked: false,
  setToggleChecked: () => {},
  highlightsVisible: true,
  disabled: false,
}

const HighlightsVisibilityContext = createContext(DEFAULT_HIGHLIGHTS_VISIBILITY_CONTEXT)

/**
 * @typedef {Object} HighlightsVisibilityContextValue
 * @property {boolean} toggleChecked - The user's stored preference (drives the toggle UI state)
 * @property {(value: boolean) => void} setToggleChecked - Updates and persists the preference
 * @property {boolean} highlightsVisible - Derived: true when preference is on or the highlights panel overlay is active
 * @property {boolean} disabled - True while the highlights panel overlay is open (toggle should be disabled)
 */

/**
 * @returns {HighlightsVisibilityContextValue}
 */
export function useHighlightsVisibilityContext() {
  return useContext(HighlightsVisibilityContext)
}

export default function HighlightsVisibilityProvider({ children }) {
  const highlightsVisibilityFlag = useFeatureFlag('rip_sharedImageViewer_highlightsVisibility')
  const preference = usePreference(SHOW_HIGHLIGHTS_PREFERENCE_KEY)
  const [localToggleChecked, setLocalToggleChecked] = useState(false)
  const { highlightsPanelOverlay, highlightsPanelOldOverlay } = useOverlayContext()

  useEffect(() => {
    if (preference !== undefined) {
      setLocalToggleChecked(preference !== false)
    }
  }, [preference])

  const setToggleChecked = useCallback((value) => {
    setLocalToggleChecked(value)
    setPreference(SHOW_HIGHLIGHTS_PREFERENCE_KEY, value)
  }, [])

  // We need to show highlights when the highlights panel overlay is active, even if the user has toggled highlights off
  const overlayActive =
    (highlightsPanelOverlay.isOpen && !highlightsPanelOverlay.transitioning) || highlightsPanelOldOverlay.isOpen

  const value = useMemo(() => {
    if (!highlightsVisibilityFlag.isOn) return DEFAULT_HIGHLIGHTS_VISIBILITY_CONTEXT

    return {
      toggleChecked: localToggleChecked,
      setToggleChecked,
      highlightsVisible: localToggleChecked || overlayActive,
      disabled: overlayActive,
    }
  }, [highlightsVisibilityFlag.isOn, localToggleChecked, setToggleChecked, overlayActive])

  return <HighlightsVisibilityContext.Provider value={value}>{children}</HighlightsVisibilityContext.Provider>
}

export { HighlightsVisibilityContext }
